import { endOfDay, isSameDay, startOfDay } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { modifyDateInTimeZone } from 'garaje/utils/date-fns-tz-utilities';

/**
 * Build the start ISO timestamp for a group desk reservation.
 *
 * The reservation must span a single calendar day in the office's timezone — so
 * "same day" is compared in that timezone, and the floor is clamped accordingly:
 *   - If `selectedDate` falls on the office-TZ calendar day of `now`, use `now`.
 *   - Otherwise, snap to the office-TZ start-of-day of `selectedDate`.
 */
export function buildGroupReservationStart(selectedDate: Date, now: Date, timezone: string): string {
  const selectedInTz = toZonedTime(selectedDate, timezone);
  const nowInTz = toZonedTime(now, timezone);

  if (isSameDay(selectedInTz, nowInTz)) {
    return now.toISOString();
  }
  return modifyDateInTimeZone(selectedDate, timezone, startOfDay).toISOString();
}

/**
 * Build the end ISO timestamp for a group desk reservation — always the
 * office-TZ end-of-day of the selected date.
 */
export function buildGroupReservationEnd(selectedDate: Date, timezone: string): string {
  return modifyDateInTimeZone(selectedDate, timezone, endOfDay).toISOString();
}

/**
 * The earliest date a user is allowed to pick — the start of the office's
 * current calendar day, regardless of the browser's timezone. The returned
 * Date is the UTC instant whose office-TZ rendering is 00:00 of "today" in
 * that timezone.
 */
export function buildGroupReservationMinDate(now: Date, timezone: string): Date {
  return modifyDateInTimeZone(now, timezone, startOfDay);
}

/**
 * The date the modal opens on — the office-TZ start-of-day of `argsDate` (or
 * "now" if none was supplied), clamped at the office-TZ minimum so the picker
 * never initializes with a value that's already disabled.
 */
export function buildGroupReservationInitialDate(argsDate: Date | undefined, now: Date, timezone: string): Date {
  const min = buildGroupReservationMinDate(now, timezone);
  if (!argsDate) {
    return min;
  }
  const officeStartOfArgsDate = modifyDateInTimeZone(argsDate, timezone, startOfDay);
  return officeStartOfArgsDate.getTime() < min.getTime() ? min : officeStartOfArgsDate;
}

/**
 * Human-readable label for a selected date — renders the office-TZ calendar
 * day, so the header and the booking line up regardless of the browser's
 * timezone.
 */
export function formatGroupReservationDate(date: Date, timezone: string): string {
  return formatInTimeZone(date, timezone, 'MMM d, yyyy');
}

/**
 * Single source of truth for the reservation interval at submit time.
 * Computes start/end against a single `now` reference and reports whether
 * `selectedDate` had to be clamped up to the office-TZ minimum (which
 * happens if the modal sat open across office-TZ midnight).
 */
export interface GroupReservationInterval {
  startISO: string;
  endISO: string;
  wasClamped: boolean;
  effectiveSelectedDate: Date;
}

export function buildGroupReservationInterval(
  selectedDate: Date,
  now: Date,
  timezone: string,
): GroupReservationInterval {
  const min = buildGroupReservationMinDate(now, timezone);
  const officeStartOfSelected = modifyDateInTimeZone(selectedDate, timezone, startOfDay);
  const wasClamped = officeStartOfSelected.getTime() < min.getTime();
  const effectiveSelectedDate = wasClamped ? min : selectedDate;

  return {
    startISO: buildGroupReservationStart(effectiveSelectedDate, now, timezone),
    endISO: buildGroupReservationEnd(effectiveSelectedDate, timezone),
    wasClamped,
    effectiveSelectedDate,
  };
}
